import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ColorPicker } from '../shared/ColorPicker';

/**
 * CanvasBackgroundEditor Component (formerly CanvasColorEditor)
 * Editor for changing the background canvas (solid color or gradient) for images with non-9:16 aspect ratios
 * Only visible when image needs a background
 */
export const CanvasColorEditor = ({
  // New gradient-aware props
  backgroundMode = 'solid',
  backgroundConfig = {},
  originalBackgroundConfig = {},
  onModeChange,
  onSolidColorChange,
  onGradientChange,
  // Legacy props for backward compatibility
  backgroundColor,
  originalBackgroundColor,
  onChange,
  // Common props
  imageUrl = null,
  brandColors = null
}) => {
  const { t } = useTranslation('approval');

  // Use new props if available, fallback to legacy for backward compatibility
  const mode = backgroundMode;
  const config = backgroundConfig;
  const originalConfig = originalBackgroundConfig;


  /**
   * Preset colors for quick selection
   */
  const basePresets = [
    { name: t('canvasColorEditor.presets.black', { defaultValue: 'Black' }), color: '#000000' },
    { name: t('canvasColorEditor.presets.white', { defaultValue: 'White' }), color: '#FFFFFF' },
    { name: t('canvasColorEditor.presets.darkGray', { defaultValue: 'Dark Gray' }), color: '#1E1E1E' },
    { name: t('canvasColorEditor.presets.lightGray', { defaultValue: 'Light Gray' }), color: '#E0E0E0' }
  ];

  // Add brand colors if available
  const brandPresets = brandColors ? [
    {
      name: t('canvasColorEditor.presets.brandText', { defaultValue: 'Brand Text Color' }),
      color: brandColors.textColor,
      isBrand: true
    },
    {
      name: t('canvasColorEditor.presets.brandBackground', { defaultValue: 'Brand Background' }),
      color: brandColors.backgroundColor,
      isBrand: true
    }
  ] : [];

  const presetColors = [...brandPresets, ...basePresets];

  return (
    <div className="canvas-background-editor space-y-4">
      {/* Background Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => onModeChange && onModeChange('solid')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'solid'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <rect x="2" y="2" width="16" height="16" rx="2" />
            </svg>
            {t('backgroundEditor.modeSolid', { defaultValue: 'Solid Color' })}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onModeChange && onModeChange('gradient')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'gradient'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <defs>
                <linearGradient id="gradient-icon" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0.4" />
                </linearGradient>
              </defs>
              <rect x="2" y="2" width="16" height="16" rx="2" fill="url(#gradient-icon)" />
            </svg>
            {t('backgroundEditor.modeGradient', { defaultValue: 'Gradient' })}
          </span>
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">
              {t('canvasColorEditor.info.title', { defaultValue: 'Canvas Mode Active' })}
            </p>
            <p className="text-amber-700">
              {t('canvasColorEditor.info.description', {
                defaultValue: 'This image has a different aspect ratio than Instagram Stories (9:16). It will be placed on a colored background canvas to fit perfectly.'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* SOLID COLOR MODE */}
      {mode === 'solid' && (
        <>
          {/* Color Picker */}
          <div>
            <ColorPicker
              value={config.backgroundColor}
              onChange={onSolidColorChange}
              label={t('canvasColorEditor.label', { defaultValue: 'Background Color' })}
              imageUrl={imageUrl}
            />
          </div>

          {/* Preset Colors */}
          <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('canvasColorEditor.presetsLabel', { defaultValue: 'Quick Presets' })}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {presetColors.map(({ name, color, isBrand }) => (
            <button
              key={color}
              type="button"
              onClick={() => onSolidColorChange && onSolidColorChange(color)}
              className={`
                flex items-center gap-2 px-3 py-2 border rounded-lg text-sm
                transition-all
                ${isBrand
                  ? 'border-purple-300 bg-purple-50 hover:border-purple-500 hover:bg-purple-100'
                  : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }
                ${config.backgroundColor === color
                  ? isBrand
                    ? 'border-purple-500 bg-purple-100 ring-2 ring-purple-200'
                    : 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : ''
                }
              `}
            >
              <div
                className="w-6 h-6 rounded border border-gray-300"
                style={{ backgroundColor: color }}
              />
              <span className={`font-medium text-xs ${isBrand ? 'text-purple-700' : ''}`}>
                {name}
                {isBrand && (
                  <svg className="w-3 h-3 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                )}
              </span>
            </button>
          ))}
        </div>
          </div>
        </>
      )}

      {/* GRADIENT MODE */}
      {mode === 'gradient' && (
        <div className="space-y-4">
          {/* Gradient Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('backgroundEditor.gradientType', { defaultValue: 'Gradient Type' })}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onGradientChange && onGradientChange({ gradientType: 'linear' })}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  config.gradientType === 'linear'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <defs>
                      <linearGradient id="linear-icon" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                    <rect x="2" y="2" width="16" height="16" rx="2" fill="url(#linear-icon)" />
                  </svg>
                  {t('backgroundEditor.linear', { defaultValue: 'Linear' })}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onGradientChange && onGradientChange({ gradientType: 'radial' })}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  config.gradientType === 'radial'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <defs>
                      <radialGradient id="radial-icon">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
                      </radialGradient>
                    </defs>
                    <circle cx="10" cy="10" r="8" fill="url(#radial-icon)" />
                  </svg>
                  {t('backgroundEditor.radial', { defaultValue: 'Radial' })}
                </span>
              </button>
            </div>
          </div>

          {/* Gradient Color Stops */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('backgroundEditor.gradientColors', { defaultValue: 'Colors' })}
            </label>
            <div className="space-y-4">
              {config.gradientColors.map((color, index) => (
                <div key={index} className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-600">
                      {t('backgroundEditor.colorStop', { defaultValue: 'Color {{number}}', number: index + 1 })}
                    </label>
                    {config.gradientColors.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newColors = config.gradientColors.filter((_, i) => i !== index);
                          onGradientChange && onGradientChange({ gradientColors: newColors });
                        }}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                        title={t('backgroundEditor.removeColor', { defaultValue: 'Remove color' })}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('backgroundEditor.remove', { defaultValue: 'Remove' })}
                      </button>
                    )}
                  </div>
                  <ColorPicker
                    value={color}
                    onChange={(newColor) => {
                      const newColors = [...config.gradientColors];
                      newColors[index] = newColor;
                      onGradientChange && onGradientChange({ gradientColors: newColors });
                    }}
                    label=""
                    imageUrl={imageUrl}
                  />
                </div>
              ))}

              {config.gradientColors.length < 4 && (
                <button
                  type="button"
                  onClick={() => {
                    const lastColor = config.gradientColors[config.gradientColors.length - 1];
                    const newColors = [...config.gradientColors, lastColor];
                    onGradientChange && onGradientChange({ gradientColors: newColors });
                  }}
                  className="w-full px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg transition-colors"
                >
                  + {t('backgroundEditor.addColor', { defaultValue: 'Add Color' })}
                </button>
              )}
            </div>
          </div>

          {/* Angle Slider (Linear only) */}
          {config.gradientType === 'linear' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('backgroundEditor.angle', { defaultValue: 'Angle' })}: {config.gradientAngle}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                step="15"
                value={config.gradientAngle}
                onChange={(e) => onGradientChange && onGradientChange({ gradientAngle: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right,
                    hsl(0, 70%, 60%),
                    hsl(60, 70%, 60%),
                    hsl(120, 70%, 60%),
                    hsl(180, 70%, 60%),
                    hsl(240, 70%, 60%),
                    hsl(300, 70%, 60%),
                    hsl(360, 70%, 60%))`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0°</span>
                <span>90°</span>
                <span>180°</span>
                <span>270°</span>
                <span>360°</span>
              </div>
            </div>
          )}

          {/* Gradient Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('backgroundEditor.preview', { defaultValue: 'Preview' })}
            </label>
            <div
              className="w-full h-24 rounded-lg border-2 border-gray-300"
              style={{
                background: config.gradientType === 'linear'
                  ? `linear-gradient(${config.gradientAngle}deg, ${config.gradientColors.join(', ')})`
                  : `radial-gradient(circle, ${config.gradientColors.join(', ')})`
              }}
            />
          </div>

          {/* Gradient Presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('backgroundEditor.gradientPresets', { defaultValue: 'Quick Presets' })}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {/* Sunset */}
              <button
                type="button"
                onClick={() => onGradientChange && onGradientChange({
                  gradientType: 'linear',
                  gradientColors: ['#FF512F', '#DD2476'],
                  gradientAngle: 45
                })}
                className="flex flex-col items-center gap-2 p-2 border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
              >
                <div
                  className="w-full h-12 rounded border border-gray-300"
                  style={{ background: 'linear-gradient(45deg, #FF512F, #DD2476)' }}
                />
                <span className="text-xs font-medium text-gray-700">
                  🌅 {t('backgroundEditor.presets.sunset', { defaultValue: 'Sunset' })}
                </span>
              </button>

              {/* Ocean */}
              <button
                type="button"
                onClick={() => onGradientChange && onGradientChange({
                  gradientType: 'linear',
                  gradientColors: ['#00D4FF', '#0076FF'],
                  gradientAngle: 135
                })}
                className="flex flex-col items-center gap-2 p-2 border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
              >
                <div
                  className="w-full h-12 rounded border border-gray-300"
                  style={{ background: 'linear-gradient(135deg, #00D4FF, #0076FF)' }}
                />
                <span className="text-xs font-medium text-gray-700">
                  🌊 {t('backgroundEditor.presets.ocean', { defaultValue: 'Ocean' })}
                </span>
              </button>

              {/* Forest */}
              <button
                type="button"
                onClick={() => onGradientChange && onGradientChange({
                  gradientType: 'linear',
                  gradientColors: ['#11998E', '#38EF7D'],
                  gradientAngle: 90
                })}
                className="flex flex-col items-center gap-2 p-2 border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
              >
                <div
                  className="w-full h-12 rounded border border-gray-300"
                  style={{ background: 'linear-gradient(90deg, #11998E, #38EF7D)' }}
                />
                <span className="text-xs font-medium text-gray-700">
                  🌲 {t('backgroundEditor.presets.forest', { defaultValue: 'Forest' })}
                </span>
              </button>

              {/* Brand Gradient (if brand colors available) */}
              {brandColors && (
                <button
                  type="button"
                  onClick={() => onGradientChange && onGradientChange({
                    gradientType: 'linear',
                    gradientColors: [brandColors.textColor, brandColors.backgroundColor],
                    gradientAngle: 45
                  })}
                  className="flex flex-col items-center gap-2 p-2 border-2 border-purple-300 bg-purple-50 rounded-lg hover:border-purple-500 hover:shadow-md transition-all"
                >
                  <div
                    className="w-full h-12 rounded border border-gray-300"
                    style={{ background: `linear-gradient(45deg, ${brandColors.textColor}, ${brandColors.backgroundColor})` }}
                  />
                  <span className="text-xs font-medium text-purple-700 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                    {t('backgroundEditor.presets.brand', { defaultValue: 'Brand' })}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CanvasColorEditor.propTypes = {
  // New gradient-aware props
  backgroundMode: PropTypes.oneOf(['solid', 'gradient']),
  backgroundConfig: PropTypes.shape({
    backgroundColor: PropTypes.string,
    gradientType: PropTypes.oneOf(['linear', 'radial']),
    gradientColors: PropTypes.arrayOf(PropTypes.string),
    gradientAngle: PropTypes.number
  }),
  originalBackgroundConfig: PropTypes.shape({
    mode: PropTypes.oneOf(['solid', 'gradient']),
    backgroundColor: PropTypes.string,
    gradientType: PropTypes.oneOf(['linear', 'radial']),
    gradientColors: PropTypes.arrayOf(PropTypes.string),
    gradientAngle: PropTypes.number
  }),
  onModeChange: PropTypes.func,
  onSolidColorChange: PropTypes.func,
  onGradientChange: PropTypes.func,
  // Legacy props (for backward compatibility)
  backgroundColor: PropTypes.string,
  originalBackgroundColor: PropTypes.string,
  onChange: PropTypes.func,
  // Common props
  imageUrl: PropTypes.string,
  brandColors: PropTypes.shape({
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    textHasBackground: PropTypes.bool
  })
};

export default CanvasColorEditor;
